import { useSyncExternalStore } from 'react';

/**
 * The open client's tax pack and number conventions (document 05, "Formats"; document 07 "India
 * GST"): India clients show `INR 12,34,567.89` (lakh and crore grouping) and India time; UAE
 * clients show `AED 1,234,567.89` and Gulf time. Before a client is open, India (the primary
 * market). The shell sets it from the client switcher; format helpers read it.
 */
export interface OrgLocale {
  taxPack: 'uae' | 'in';
  currency: string;
  /** Indian digit grouping (lakh, crore). */
  indianGrouping: boolean;
  /** India: the organisation's GST state code. */
  stateCode: string | null;
  /** IANA time zone timestamps are shown in. */
  timeZone: string;
}

const UAE: OrgLocale = {
  taxPack: 'uae',
  currency: 'AED',
  indianGrouping: false,
  stateCode: null,
  timeZone: 'Asia/Dubai',
};
const INDIA: OrgLocale = {
  taxPack: 'in',
  currency: 'INR',
  indianGrouping: true,
  stateCode: null,
  timeZone: 'Asia/Kolkata',
};
let current: OrgLocale = INDIA;
const listeners = new Set<() => void>();

export function orgLocale(): OrgLocale {
  return current;
}

/** The home currency of the open client ("AED", "INR"). */
export const homeCurrency = (): string => current.currency;

export const isIndia = (): boolean => current.taxPack === 'in';

/**
 * Sets the locale; returns true when it changed. The shell calls it while rendering (so the page
 * below formats with the right client on its first render) and then notifies subscribers from
 * an effect (notifyOrgLocale), never during render.
 */
export function setOrgLocale(
  client: {
    baseCurrency?: string;
    taxPack?: string;
    country?: string;
    stateCode?: string | null;
  } | null,
): boolean {
  const uae = client?.taxPack === 'uae' || (!client?.taxPack && client?.country === 'AE');
  const next: OrgLocale = uae
    ? { ...UAE, currency: client?.baseCurrency ?? 'AED' }
    : {
        ...INDIA,
        currency: client?.baseCurrency ?? 'INR',
        stateCode: client?.stateCode ?? null,
      };
  if (
    next.taxPack === current.taxPack &&
    next.currency === current.currency &&
    next.stateCode === current.stateCode
  )
    return false;
  current = next;
  return true;
}

export function notifyOrgLocale(): void {
  listeners.forEach((l) => l());
}

export function useOrgLocale(): OrgLocale {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    orgLocale,
    orgLocale,
  );
}

/**
 * The open client's home currency as a hook (re-renders when the client changes): every home
 * amount, the exchange rate on foreign-currency documents and the revaluation are in it.
 */
export function useHomeCurrency(): string {
  return useOrgLocale().currency;
}

/** Groups the integer digits of a plain number string: "1234567" → "1,234,567" or "12,34,567". */
export function groupDigits(int: string, indian = current.indianGrouping): string {
  if (!indian || int.length <= 3) return int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const last3 = int.slice(-3);
  return `${int.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`;
}

const CURRENCY_NAMES: Record<string, string> = { INR: 'Indian rupee', AED: 'UAE dirham' };

/** "INR – Indian rupee", "AED – UAE dirham". */
export function homeCurrencyLabel(code = current.currency): string {
  return CURRENCY_NAMES[code] ? `${code} – ${CURRENCY_NAMES[code]}` : code;
}

/** "GST" for India clients, "VAT" for UAE clients (labels on shared forms). */
export const taxWord = (): string => (current.taxPack === 'in' ? 'GST' : 'VAT');
